import { EmailInboxView, EmailMessageDirection } from '@/gen/cortado/v1/emails_pb'
import type { EmailInboxViewCounts, EmailThreadLite } from '@/gen/cortado/v1/emails_pb'
import { ParentTypeEnum } from '@/gen/cortado/v1/settings_pb'

/** Inbox tabs, in display order. "not-in-cortado" is the CPRO-789 sender list. */
export type InboxTab = 'needs-review' | 'mine' | 'all' | 'snoozed' | 'muted' | 'not-in-cortado'

export const DEFAULT_INBOX_TAB: InboxTab = 'mine'

export const INBOX_TABS: ReadonlyArray<{ value: InboxTab; label: string; view?: EmailInboxView }> = [
  { value: 'needs-review', label: 'Needs review', view: EmailInboxView.NEEDS_REVIEW },
  { value: 'mine', label: 'Mine', view: EmailInboxView.MINE },
  { value: 'all', label: 'All', view: EmailInboxView.ALL },
  { value: 'not-in-cortado', label: 'Not in Cortado' },
  { value: 'snoozed', label: 'Snoozed', view: EmailInboxView.SNOOZED },
  { value: 'muted', label: 'Muted', view: EmailInboxView.MUTED },
]

export function inboxTabView(tab: InboxTab): EmailInboxView | undefined {
  return INBOX_TABS.find((t) => t.value === tab)?.view
}

/** The tab's size from the server, or undefined before the first load. */
export function inboxTabCount(tab: InboxTab, counts: EmailInboxViewCounts | undefined) {
  if (!counts) return undefined
  switch (tab) {
    case 'needs-review':
      return counts.needsReview
    case 'mine':
      return counts.mine
    case 'all':
      return counts.all
    case 'snoozed':
      return counts.snoozed
    case 'muted':
      return counts.muted
    default:
      return undefined
  }
}

export type SnoozeOption = '1d' | '3d' | 'next-week'

export const SNOOZE_OPTIONS: ReadonlyArray<{ value: SnoozeOption; label: string }> = [
  { value: '1d', label: '1 day' },
  { value: '3d', label: '3 days' },
  { value: 'next-week', label: 'Next week' },
]

/** Snoozed threads come back at 8am local time. */
const SNOOZE_WAKE_HOUR = 8

function atWakeHour(date: Date): Date {
  const result = new Date(date)
  result.setHours(SNOOZE_WAKE_HOUR, 0, 0, 0)
  return result
}

/**
 * When a snooze option ends: 1 day and 3 days count from today, next week is
 * the coming Monday. Always 8am local time.
 */
export function snoozeUntil(option: SnoozeOption, now: Date = new Date()): Date {
  const date = new Date(now)
  switch (option) {
    case '1d':
      date.setDate(date.getDate() + 1)
      break
    case '3d':
      date.setDate(date.getDate() + 3)
      break
    case 'next-week': {
      // getDay: Sunday 0 ... Saturday 6. Monday is always 1-7 days ahead.
      const daysToMonday = ((8 - date.getDay()) % 7) || 7
      date.setDate(date.getDate() + daysToMonday)
      break
    }
  }
  return atWakeHour(date)
}

/** A custom snooze picks a day; it ends at 8am that day. */
export function customSnoozeUntil(day: Date): Date {
  return atWakeHour(day)
}

/** Whose move it is on the thread, from its latest message. */
export type InboxThreadBadge = 'reply-needed' | 'waiting' | null

export function inboxThreadBadge(thread: Pick<EmailThreadLite, 'lastMessageDirection'>): InboxThreadBadge {
  switch (thread.lastMessageDirection) {
    case EmailMessageDirection.INBOUND:
      return 'reply-needed'
    case EmailMessageDirection.OUTBOUND:
      return 'waiting'
    default:
      return null
  }
}

/** Who the conversation is with: the linked contact, else the first customer on it. */
export function inboxThreadContactLabel(thread: Pick<EmailThreadLite, 'contactName' | 'participants'>): string {
  const contactName = thread.contactName?.trim()
  if (contactName) return contactName
  const customer = thread.participants.find((p) => p.type !== 'user') ?? thread.participants[0]
  return customer?.name?.trim() || customer?.email || 'Unknown sender'
}

/** "Wedding" for the linked lead or booking, or null when there is none. */
export function inboxThreadEventLabel(
  thread: Pick<EmailThreadLite, 'linkedEntityType' | 'linkedEventType'>,
): string | null {
  if (thread.linkedEntityType === undefined) return null
  const eventType = thread.linkedEventType?.trim()
  if (eventType) return eventType
  return thread.linkedEntityType === ParentTypeEnum.PARENT_TYPE_BOOKING ? 'Booking' : 'Lead'
}

/**
 * The record a reply files under (its attachments upload there): the linked
 * lead or booking, else the contact. Null when the thread has neither, so the
 * Inbox asks for a link first.
 */
export function inboxThreadReplyParent(
  thread: Pick<EmailThreadLite, 'linkedEntityType' | 'linkedEntityId' | 'contactId'>,
): { parentType: ParentTypeEnum; parentId: string } | null {
  if (thread.linkedEntityType !== undefined && thread.linkedEntityId) {
    return { parentType: thread.linkedEntityType, parentId: thread.linkedEntityId }
  }
  if (thread.contactId) {
    return { parentType: ParentTypeEnum.PARENT_TYPE_CONTACT, parentId: thread.contactId }
  }
  return null
}
